import { cfgBrand, FeatureSet, stateFeatureSet } from '@infinitro/config';
import { translate, Verbiage } from '@infinitro/content';
import { Api, isSuccessStatus, loggedIn, startUiTimer, Status } from '@infinitro/global';
import { nationalSignificantDigits } from '@infinitro/input';
import { derived, get, writable } from 'svelte/store';
import { ActionType, AsyncGetPhoneCountryConfig, ConnectConfirmPhone, ConnectStartPhone } from './api/api';
import { log, stateError, stateFatalError } from './state';
/**
 * Phone verification is preview-only until ready for production.
 * Disabled when FeatureSet is Release (always the case on prod).
 */
export const isPhoneVerificationEnabled = () => get(stateFeatureSet) === FeatureSet.Preview;
export const statePhoneVerificationEnabled = derived(stateFeatureSet, ($featureSet) => $featureSet === FeatureSet.Preview);
/** Last phoneId returned from connectStartPhone. */
export const statePhoneEntityId = writable(undefined);
function toPhoneNumberInt(countryCode, nationalNumber) {
    const digits = nationalSignificantDigits(countryCode, nationalNumber);
    if (!digits)
        return null;
    const value = Number(digits);
    if (!Number.isSafeInteger(value) || value <= 0)
        return null;
    return value;
}
export const handleRequestPhoneVerifyCode = async (countryCode, nationalNumber, options = {}) => {
    log(`library default handleRequestPhoneVerifyCode: +${countryCode} ${nationalNumber}`);
    const isLoggedIn = get(loggedIn);
    if (!isLoggedIn) {
        const errorMessage = translate(Verbiage.please_contact_support) ?? 'Please sign in to verify your phone';
        stateError.set(errorMessage);
        stateFatalError.set(true);
        return [false, Status.FAILED, errorMessage, undefined];
    }
    // Resend (OTP modal): phoneId only.
    // New/changed number: countryCode + phoneNumber only — never reuse a prior phoneId
    // (mixing both paths returns REQUIRED_PARAMS).
    const resendPhoneId = options.phoneId?.trim() || undefined;
    let codeNum;
    let phoneNumberInt;
    if (!resendPhoneId) {
        codeNum = Number(String(countryCode).replace(/\D/g, ''));
        const parsed = toPhoneNumberInt(countryCode, nationalNumber);
        if (!Number.isFinite(codeNum) || (codeNum ?? 0) <= 0 || parsed == null) {
            const errorMessage = translate(Verbiage.verify_mobile_number);
            stateError.set(errorMessage);
            return [false, Status.FAILED, errorMessage, undefined];
        }
        phoneNumberInt = parsed;
    }
    try {
        const result = await ConnectStartPhone({
            variables: {
                forBonus: options.forBonus ?? false,
                ...(options.actionType != null ? { actionType: options.actionType } : {}),
                ...(resendPhoneId
                    ? { phoneId: resendPhoneId }
                    : { countryCode: codeNum, phoneNumber: phoneNumberInt })
            },
            context: { api: Api.player }
        });
        const data = result?.data?.connectStartPhone;
        if (data?.status === Status.TOO_MANY_ATTEMPTS) {
            stateFatalError.set(true);
            const errorMessage = translate(Verbiage.too_many_attempts_please_try_again_later);
            stateError.set(errorMessage);
            return [false, Status.TOO_MANY_ATTEMPTS, errorMessage, undefined];
        }
        if (isSuccessStatus(data?.status)) {
            stateError.set(undefined);
            stateFatalError.set(false);
            const nextPhoneId = data?.phoneId ?? resendPhoneId;
            if (nextPhoneId)
                statePhoneEntityId.set(nextPhoneId);
            if (data?.timeRemaining) {
                startUiTimer(data.timeRemaining, data.timeRemaining);
            }
            else {
                startUiTimer();
            }
            return [true, data?.status, undefined, nextPhoneId];
        }
        const errorMessage = data?.status ?? 'unknown error';
        stateError.set(errorMessage);
        return [false, data?.status ?? Status.FAILED, errorMessage, undefined];
    }
    catch (e) {
        console.error('error requesting phone verify code', e);
        const errorMessage = e?.message ??
            e?.toString() ??
            'unknown error requesting phone verification code';
        stateError.set(errorMessage);
        return [false, Status.FAILED, errorMessage, undefined];
    }
};
export const handleCheckPhoneVerifyCode = async (phoneId, otpCode, actionType) => {
    log(`library default handleCheckPhoneVerifyCode: ${phoneId} OTP: ${otpCode}`);
    const isLoggedIn = get(loggedIn);
    if (!isLoggedIn) {
        const errorMessage = translate(Verbiage.please_contact_support) ?? 'Please sign in to verify your phone';
        stateError.set(errorMessage);
        stateFatalError.set(true);
        return [false, Status.FAILED, errorMessage, undefined];
    }
    if (!phoneId) {
        const errorMessage = translate(Verbiage.error);
        stateError.set(errorMessage);
        return [false, Status.FAILED, errorMessage, undefined];
    }
    try {
        const result = await ConnectConfirmPhone({
            variables: {
                phoneId,
                code: otpCode,
                ...(actionType != null ? { actionType } : {})
            },
            context: { api: Api.player }
        });
        const data = result?.data?.connectConfirmPhone;
        if (data?.status === Status.TOO_MANY_ATTEMPTS) {
            stateFatalError.set(true);
            const errorMessage = translate(Verbiage.too_many_attempts_please_try_again_later);
            stateError.set(errorMessage);
            return [false, Status.TOO_MANY_ATTEMPTS, errorMessage, undefined];
        }
        else if (data?.status === Status.FAILED) {
            const errorMessage = translate(Verbiage.incorrect_code) + ' ' + translate(Verbiage.please_try_again);
            stateError.set(errorMessage);
            return [false, Status.FAILED, errorMessage, undefined];
        }
        else if (data?.status === Status.CONFLICT) {
            const errorMessage = translate(Verbiage.please_contact_player_support);
            stateError.set(errorMessage);
            stateFatalError.set(true);
            return [true, Status.CONFLICT, errorMessage, undefined];
        }
        else if (isSuccessStatus(data?.status)) {
            stateFatalError.set(false);
            stateError.set(undefined);
            return [true, data?.status, undefined, data?.id ?? phoneId];
        }
        else if (data?.status) {
            const errorMessage = data?.status ?? 'unknown error';
            stateError.set(errorMessage);
            return [isSuccessStatus(data?.status), data?.status, errorMessage, undefined];
        }
        return [false, Status.FAILED, 'unknown error', undefined];
    }
    catch (e) {
        console.error('error checking phone verify code', e);
        const errorMessage = e?.message ??
            e?.toString() ??
            'unknown error checking phone verification code';
        stateError.set(errorMessage);
        return [false, Status.FAILED, errorMessage, undefined];
    }
};
export async function loadPhoneCountryConfig(forBonus = true) {
    const result = await AsyncGetPhoneCountryConfig({
        variables: { brand: cfgBrand(), forBonus },
        context: { api: Api.player },
        fetchPolicy: 'network-only'
    });
    return result?.data?.getPhoneCountryConfig ?? [];
}
/** @deprecated Prefer loadPhoneCountryConfig(true) */
export async function loadBonusPhoneCountryConfig() {
    return loadPhoneCountryConfig(true);
}
